import FormInput from '../../../components/FormInput';
import FormEntry from '../../../components/FormEntry';
import { useEffect, useState } from 'react';
import { HiXMark } from 'react-icons/hi2';
import { useFormContext } from 'react-hook-form';
import SectionHeader from 'components/SectionHeader';
import { useConfigsId } from '../../../hooks/useConfigs';
import { getSingletonConfigCalibrationId } from '../../../hooks/useSingletonSessions';
import { CalibrationRefType, SessionAnalysisTypeUtils, SessionAnalysisType } from '../../../types/SessionAnalysisType';
import { useSessions, useSessionCalcData } from '../../../hooks/useSessions';

type SelectionMode = 'team' | 'session';

export default function NewSessionCalibrationConfig(props: {
  ridersById: Map<any, any>;
  onRiderWarningChange?: (show: boolean) => void;
}) {
  const { ridersById } = props;
  const { watch, setValue, getValues, register } = useFormContext();
  const [warning, setWarning] = useState<string | null>(null);
  const [expectedRange, setExpectedRange] = useState<string | null>(null);
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('team');

  // Watch for analysis_type to determine if indoor session
  const analysisType = watch('analysis_type');
  const isIndoorSession = Number(analysisType) >= SessionAnalysisType.TRAVERSE_INDOORS;

  // For Team Calibration mode
  const [teamCalibrationConfig, setTeamCalibrationConfig] = useState<any>(null);

  // For Session/Config mode
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [availableConfigs, setAvailableConfigs] = useState<any[]>([]);

  // Fetch data
  const { data: configs } = useConfigsId();
  const { data: sessions } = useSessions();
  // Only fetch session calc data if we have a valid selected session ID
  // Pass a dummy ID if none selected to prevent API call with empty ID
  const { data: selectedSessionCalcData } = useSessionCalcData(selectedSessionId || 'no-session-selected');

  // Check for warnings whenever relevant fields change
  const checkWarning = (value: { combined_mass?: number | string; rider_id?: string; calibration_id?: string }) => {
    // Get current form values to access rider_id from the main form
    const formValues = getValues();
    const riderId = value.rider_id || formValues.rider_id;
    const combinedMassValue = value.combined_mass || formValues.combined_mass;
    const calibrationId = value.calibration_id || formValues.calibration_id;

    // Don't show warnings if any required field is blank
    if (!riderId || !calibrationId || combinedMassValue === undefined || combinedMassValue === '') {
      setWarning(null);
      return;
    }

    const riderMass = riderId ? ridersById.get(riderId)?.mass || 0 : 0;
    const combinedMass = typeof combinedMassValue === 'string' ? parseFloat(combinedMassValue) : combinedMassValue;

    if (isNaN(combinedMass)) {
      setWarning(null);
      return;
    }
    const equipmentMass = combinedMass - riderMass;
    const rangeLower = riderMass + 6;
    const rangeUpper = riderMass + 15;
    const warning = `Combined ${combinedMass.toFixed(1)} = Rider ${riderMass}kg +
     Equipment ${equipmentMass.toFixed(1)}kg`;
    if (equipmentMass < 6 || equipmentMass > 12) {
      setWarning(warning);
      setExpectedRange(`Expected ${rangeLower.toFixed(1)} to ${rangeUpper.toFixed(1)}`);
    } else {
      setWarning(null);
      setExpectedRange(null);
    }
  };

  useEffect(() => {
    const subscription = watch(
      (value: { combined_mass?: number | string; rider_id?: string; calibration_id?: string }) => {
        checkWarning(value);
      },
    );
    return () => subscription.unsubscribe();
  }, [watch, ridersById, getValues]);

  useEffect(() => {
    checkWarning(getValues());
  }, [ridersById, getValues]);

  // Handle mode change
  const handleModeChange = (mode: SelectionMode) => {
    setSelectionMode(mode);
    // Clear form values when switching modes
    setValue('calibration_id', '', { shouldValidate: false });
    setValue('combined_mass', '', { shouldValidate: false });
    setValue('crr', '', { shouldValidate: false });
    setValue('source_session_id', '', { shouldValidate: false }); // Clear the session dropdown
    setSelectedSessionId('');
    setAvailableConfigs([]);
  };

  // Fetch team calibration config when in team mode
  useEffect(() => {
    if (selectionMode !== 'team') return;

    const fetchTeamCalibration = async () => {
      try {
        const teamCalibrationId = await getSingletonConfigCalibrationId(CalibrationRefType.TEAM);

        // Team calibration loaded: teamCalibrationId
        if (teamCalibrationId && configs) {
          const teamConfig = configs.find((c) => c.id === teamCalibrationId);
          if (teamConfig) {
            setTeamCalibrationConfig(teamConfig);
            // Automatically set the calibration_id and related fields
            setValue('calibration_id', teamConfig.id, { shouldValidate: true });
            setValue('combined_mass', teamConfig.combined_mass, { shouldValidate: true });
            setValue('crr', teamConfig.crr, { shouldValidate: true });
          }
        }
      } catch (error) {
        console.error('Error fetching team calibration:', error);
      }
    };

    if (configs && configs.length > 0) {
      fetchTeamCalibration();
    }
  }, [configs, setValue, selectionMode]);

  // Watch for session selection changes
  useEffect(() => {
    if (selectionMode !== 'session') return;

    const subscription = watch((value, { name }) => {
      if (name === 'source_session_id' && value.source_session_id) {
        // Session field changed: value.source_session_id
        setSelectedSessionId(value.source_session_id);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, selectionMode]);

  // Update available configs when session is selected (for session mode)
  useEffect(() => {
    if (selectionMode !== 'session') return;

    if (selectedSessionId && selectedSessionId !== 'no-session-selected') {
      // Session selected: selectedSessionId
      // Session data received: selectedSessionCalcData

      if (selectedSessionCalcData && selectedSessionCalcData.configs) {
        // Configs found: selectedSessionCalcData.configs.length configs
        setAvailableConfigs(selectedSessionCalcData.configs);
      } else {
        // No configs found for session: selectedSessionId
        setAvailableConfigs([]);
      }
    }

    // Clear calibration selection when session changes
    if (selectedSessionId) {
      setValue('calibration_id', '', { shouldValidate: false });
      setValue('combined_mass', '', { shouldValidate: false });
      setValue('crr', '', { shouldValidate: false });
    }
  }, [selectedSessionCalcData, selectedSessionId, setValue, selectionMode]);

  // Watch for changes in calibration selection (for session mode)
  useEffect(() => {
    if (selectionMode !== 'session') return;

    const subscription = watch((value: any, info: any) => {
      if (info.name === 'calibration_id' && value.calibration_id) {
        const selectedCalibration = availableConfigs.find((c) => c.id === value.calibration_id);
        if (selectedCalibration) {
          setValue('combined_mass', selectedCalibration.combined_mass, { shouldValidate: true });
          setValue('crr', selectedCalibration.crr, { shouldValidate: true });
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue, availableConfigs, selectionMode]);

  return (
    <div className="cr-mb-4 cr-flex cr-w-full cr-flex-[1_1] cr-flex-col">
      <div className="cr-h-full cr-w-full">
        <div className="cr-flex cr-flex-col">
          <SectionHeader title="Calibration Configuration" />

          <div className="cr-flex cr-flex-col cr-rounded-lg cr-bg-white cr-p-2 cr-text-center cr-shadow-md md:cr-w-[640px] md:cr-text-left">
            {/* Radio buttons for selection mode */}
            <FormEntry label="Copy Configuration From">
              <div className="cr-flex cr-flex-col cr-gap-2">
                <label className="cr-flex cr-cursor-pointer cr-items-center">
                  <input
                    type="radio"
                    name="selectionMode"
                    value="team"
                    checked={selectionMode === 'team'}
                    onChange={() => handleModeChange('team')}
                    className="cr-mr-2"
                  />
                  <span>Default Team Calibration</span>
                </label>
                <label className="cr-flex cr-cursor-pointer cr-items-center">
                  <input
                    type="radio"
                    name="selectionMode"
                    value="session"
                    checked={selectionMode === 'session'}
                    onChange={() => handleModeChange('session')}
                    className="cr-mr-2"
                  />
                  <span>Select from Session</span>
                </label>
              </div>
            </FormEntry>

            {/* Team Calibration Mode */}
            {selectionMode === 'team' && (
              <FormEntry label="Calibration">
                <div className="cr-flex cr-items-center cr-rounded-md cr-border cr-border-gray-300 cr-bg-gray-50 cr-px-3 cr-py-2">
                  <span className="cr-text-gray-700">
                    {teamCalibrationConfig
                      ? `Team Calibration: ${teamCalibrationConfig.description} ` +
                        `(${teamCalibrationConfig.combined_mass} kg, CRR: ${teamCalibrationConfig.crr})`
                      : 'Loading Team Calibration...'}
                  </span>
                </div>
                {/* Hidden input to maintain form validation */}
                <input type="hidden" {...register('calibration_id', { required: 'Calibration is Required' })} />
              </FormEntry>
            )}

            {/* Session/Config Mode */}
            {selectionMode === 'session' && (
              <>
                <FormEntry label="Select Session">
                  <FormInput
                    fieldName="source_session_id"
                    options={Array.from(sessions || [])
                      .filter((session) => session.analysis_type >= SessionAnalysisTypeUtils.singletonLevel())
                      .reverse()}
                    optionLabelFn={(session) => `${session.date} - ${session.description}`}
                    emptyOption="Select a Session"
                    customClass="cr-w-full md:cr-w-128"
                    required="Session is Required"
                  />
                </FormEntry>

                {selectedSessionId && (
                  <FormEntry label="Select Configuration">
                    <FormInput
                      fieldName="calibration_id"
                      options={availableConfigs}
                      optionLabelFn={(config) =>
                        `${config.letter} - ${config.description} (${config.combined_mass} kg, CRR: ${config.crr})`
                      }
                      emptyOption="Select a Configuration"
                      customClass="cr-w-full md:cr-w-128"
                      required="Configuration is Required"
                    />
                  </FormEntry>
                )}
              </>
            )}

            {/* Common fields */}
            <FormEntry label="Combined Mass">
              <div className="cr-flex cr-items-center cr-gap-2">
                <FormInput fieldName="combined_mass" required="Combined Mass is Required" inputType="number" />
                {expectedRange && (
                  <span className="cr-whitespace-nowrap cr-text-sm cr-text-gray-600">{expectedRange}</span>
                )}
              </div>
              {warning && (
                <div className="cr-mt-2 cr-flex cr-items-center cr-justify-between cr-rounded-md cr-bg-yellow cr-p-2 cr-text-sm cr-text-black">
                  <span>{warning}</span>
                  <span
                    onClick={() => {
                      setWarning(null);
                      setExpectedRange(null);
                    }}
                    className="cr-ml-2 cr-flex cr-h-5 cr-w-5 cr-flex-shrink-0 cr-items-center cr-justify-center cr-rounded-full cr-text-red-400 hover:cr-bg-red-100"
                  >
                    <HiXMark className="cr-h-4 cr-w-4" />
                  </span>
                </div>
              )}
            </FormEntry>
            <FormEntry label="CRR">
              <FormInput fieldName="crr" inputType="number" />
            </FormEntry>

            {/* Speed Sensor Location - for all session types */}
            <FormEntry label="Speed Sensor Location">
              <div className="cr-flex cr-flex-row cr-gap-4">
                <label className="cr-flex cr-cursor-pointer cr-items-center">
                  <input
                    type="radio"
                    value="1"
                    defaultChecked
                    {...register('speed_sensor_location')}
                    className="cr-mr-2"
                  />
                  <span>Front</span>
                </label>
                <label className="cr-flex cr-cursor-pointer cr-items-center">
                  <input type="radio" value="0" {...register('speed_sensor_location')} className="cr-mr-2" />
                  <span>Rear</span>
                </label>
                <label className="cr-flex cr-cursor-pointer cr-items-center">
                  <input type="radio" value="-1" {...register('speed_sensor_location')} className="cr-mr-2" />
                  <span>None</span>
                </label>
              </div>
            </FormEntry>

            {/* Indoor Session Parameters */}
            {isIndoorSession && (
              <>
                <FormEntry label="Wheel Circumference (m)">
                  <FormInput fieldName="wheel_circumference" inputType="number" defaultValue={2.1} />
                </FormEntry>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
